import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { SettingsData, DailySchedule, PrivateBlock } from '../types/types';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';

type SettingsTab = 'services' | 'schedule' | 'privateEvents' | 'api';

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SettingsData>({
    gocartSlotDurationMinutes: 8,
    gocartSlotIntervalMinutes: 15,
    simulatorSlotIntervalMinutes: 15,
    playhouseSlotIntervalMinutes: 15,
    vrSlotIntervalMinutes: 15,
    availableKarts: 8,
    availableSimulators: 3,
    availablePlayhouses: 4,
    availableVr: 6,
    simulatorSlotDurationMinutes: 15,
    playhouseSlotDurationMinutes: 15,
    vrSlotDurationMinutes: 15,
    schedule: {
      monday: { isOpen: true, openingHour: 10, closingHour: 20 },
      tuesday: { isOpen: true, openingHour: 10, closingHour: 20 },
      wednesday: { isOpen: true, openingHour: 10, closingHour: 20 },
      thursday: { isOpen: true, openingHour: 10, closingHour: 20 },
      friday: { isOpen: true, openingHour: 10, closingHour: 20 },
      saturday: { isOpen: true, openingHour: 10, closingHour: 20 },
      sunday: { isOpen: true, openingHour: 10, closingHour: 20 },
    },
    privateBlocks: [],  // Új üres tömb a zártkörű időszakokhoz
    apiAccessToken: ''  // API hozzáférési token
  });
  const [activeTab, setActiveTab] = useState<SettingsTab>('services');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [apiUrl, setApiUrl] = useState('');

  const { currentUser, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }

    fetchSettings();
  }, [currentUser, isAdmin, navigate]);

  useEffect(() => {
    // API URL beállítása - Firebase Cloud Functions URL formátum
    if (settings.apiAccessToken) {
      // A projektazonosító a firebase konfiguráció alapján
      const projectId = 'shskartadmin'; // A projektazonosító a .firebaserc-ból
      const region = 'us-central1'; // Alapértelmezett régió
      
      // Cloud Functions URL formátum
      setApiUrl(`https://${region}-${projectId}.cloudfunctions.net/bookings?token=${settings.apiAccessToken}`);
    }
  }, [settings.apiAccessToken]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settingsDoc = await getDoc(doc(db, 'settings', 'bookings'));

      if (settingsDoc.exists()) {
        const settingsData = settingsDoc.data() as Partial<SettingsData>;
        // Alapértelmezett értékek beállítása ha hiányoznak
        
        // Ensure privateBlocks have notes field
        const privateBlocksWithNotes = (settingsData.privateBlocks || []).map(block => ({
          ...block,
          notes: (block as any).notes || ''  // Use type assertion to fix TypeScript error
        }));
        
        setSettings({
          gocartSlotDurationMinutes: settingsData.gocartSlotDurationMinutes ?? 8,
          gocartSlotIntervalMinutes: settingsData.gocartSlotIntervalMinutes ?? 15,
          simulatorSlotIntervalMinutes: settingsData.simulatorSlotIntervalMinutes ?? 15,
          playhouseSlotIntervalMinutes: settingsData.playhouseSlotIntervalMinutes ?? 15,
          simulatorSlotDurationMinutes: settingsData.simulatorSlotDurationMinutes ?? 15,
          playhouseSlotDurationMinutes: settingsData.playhouseSlotDurationMinutes ?? 15,
          vrSlotDurationMinutes: settingsData.vrSlotDurationMinutes ?? 15,
          vrSlotIntervalMinutes: settingsData.vrSlotIntervalMinutes ?? 15,
          availableKarts: settingsData.availableKarts ?? 8,
          availableSimulators: settingsData.availableSimulators ?? 3,
          availablePlayhouses: settingsData.availablePlayhouses ?? 4,
          availableVr: settingsData.availableVr ?? 6,
          schedule: settingsData.schedule ?? {
            monday: { isOpen: true, openingHour: 10, closingHour: 20 },
            tuesday: { isOpen: true, openingHour: 10, closingHour: 20 },
            wednesday: { isOpen: true, openingHour: 10, closingHour: 20 },
            thursday: { isOpen: true, openingHour: 10, closingHour: 20 },
            friday: { isOpen: true, openingHour: 10, closingHour: 20 },
            saturday: { isOpen: true, openingHour: 10, closingHour: 20 },
            sunday: { isOpen: true, openingHour: 10, closingHour: 20 },
          },
          privateBlocks: privateBlocksWithNotes,
          apiAccessToken: settingsData.apiAccessToken ?? ''
        });
      }
    } catch (error) {
      console.error('Hiba a beállítások lekérése során:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: parseInt(value)
    }));
  };

  const handleScheduleChange = (
    day: keyof SettingsData['schedule'],
    field: keyof DailySchedule,
    value: boolean | number
  ) => {
    setSettings(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAdmin) return;

    try {
      setSaving(true);
      await setDoc(doc(db, 'settings', 'bookings'), settings);

      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Hiba a beállítások mentése során:', error);
    } finally {
      setSaving(false);
    }
  };

  // Mobil menü megnyitása/bezárása
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleAddPrivateBlock = () => {
    setSettings(prev => ({
      ...prev,
      privateBlocks: [
        ...prev.privateBlocks,
        {
          date: '',
          startHour: 10,
          endHour: 11,
          description: '',
          notes: '',  // Add empty notes field
          serviceType: 'gokart'  // Alapértelmezett érték
        }
      ]
    }));
  };

  const handleUpdatePrivateBlock = (
    index: number,
    field: keyof PrivateBlock,
    value: string | number
  ) => {
    setSettings(prev => ({
      ...prev,
      privateBlocks: prev.privateBlocks.map((block, i) =>
        i === index ? { ...block, [field]: value } : block
      )
    }));
  };

  const handleRemovePrivateBlock = (index: number) => {
    setSettings(prev => ({
      ...prev,
      privateBlocks: prev.privateBlocks.filter((_, i) => i !== index)
    }));
  };

  // Adjunk hozzá egy segédfüggvényt a nap meghatározásához
  const getDayOfWeek = (dateString: string): keyof SettingsData['schedule'] => {
    const days: (keyof SettingsData['schedule'])[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const date = new Date(dateString);
    return days[date.getDay()];
  };

  // API token generálása
  const generateApiToken = () => {
    // Hosszú, bonyolult token generálása
    const token = `${uuidv4()}-${uuidv4()}-${Date.now()}`;
    
    setSettings(prev => ({
      ...prev,
      apiAccessToken: token
    }));

    // Token mentése azonnal a Firestore-ba
    setDoc(doc(db, 'settings', 'bookings'), {
      ...settings,
      apiAccessToken: token
    });

    return token;
  };

  // Token másolása a vágólapra
  const copyTokenToClipboard = () => {
    navigator.clipboard.writeText(apiUrl);
    setTokenCopied(true);
    setTimeout(() => {
      setTokenCopied(false);
    }, 3000);
  };

  // Token újragenerálása
  const regenerateToken = () => {
    if (window.confirm('Biztosan új tokent szeretnél generálni? A régi token ezután érvénytelenné válik.')) {
      generateApiToken();
    }
  };
  
  // Mai napi foglalások tesztelése
  const testApiEndpoint = () => {
    if (settings.apiAccessToken) {
      // Cloud Functions URL közvetlen tesztelése
      window.open(apiUrl, '_blank');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'services':
  return (
          <div>
            <div className="mb-2 px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg text-blue-700 text-sm">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Itt állíthatod be a különböző szolgáltatások időtartamait, időközöket és elérhető eszközök számát.
            </div>
                </div>
            
                  {/* Gokart beállítások */}
            <div className="mb-8 p-6 bg-white rounded-lg shadow-sm border-t-4 border-t-red-500 border-r border-b border-l border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h2 className="text-lg font-medium text-gray-900">Gokart beállítások</h2>
              </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Futam hossza (perc)
                        </label>
                        <input
                          type="number"
                          name="gocartSlotDurationMinutes"
                          value={settings.gocartSlotDurationMinutes}
                          onChange={handleInputChange}
                          min="1"
                          max="60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Időközök (perc)
                        </label>
                        <input
                          type="number"
                          name="gocartSlotIntervalMinutes"
                          value={settings.gocartSlotIntervalMinutes}
                          onChange={handleInputChange}
                          min="5"
                          max="60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Elérhető gokartok száma
                        </label>
                        <input
                          type="number"
                          name="availableKarts"
                          value={settings.availableKarts}
                          onChange={handleInputChange}
                          min="1"
                          max="20"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* VR beállítások */}
            <div className="mb-8 p-6 bg-white rounded-lg shadow-sm border-t-4 border-t-blue-500 border-r border-b border-l border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-lg font-medium text-gray-900">VR beállítások</h2>
              </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Foglalás hossza (perc)
                        </label>
                        <input
                          type="number"
                          name="vrSlotDurationMinutes"
                          value={settings.vrSlotDurationMinutes}
                          onChange={handleInputChange}
                          min="5"
                          max="60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Időközök (perc)
                        </label>
                        <input
                          type="number"
                          name="vrSlotIntervalMinutes"
                          value={settings.vrSlotIntervalMinutes}
                          onChange={handleInputChange}
                          min="5"
                          max="60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Elérhető VR eszközök száma
                        </label>
                        <input
                          type="number"
                          name="availableVr"
                          value={settings.availableVr}
                          onChange={handleInputChange}
                          min="1"
                          max="20"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Szimulátor beállítások */}
            <div className="mb-8 p-6 bg-white rounded-lg shadow-sm border-t-4 border-t-orange-500 border-r border-b border-l border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                  </svg>
                </div>
                <h2 className="text-lg font-medium text-gray-900">Szimulátor beállítások</h2>
              </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Foglalás hossza (perc)
                        </label>
                        <input
                          type="number"
                          name="simulatorSlotDurationMinutes"
                          value={settings.simulatorSlotDurationMinutes}
                          onChange={handleInputChange}
                          min="5"
                          max="60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Időközök (perc)
                        </label>
                        <input
                          type="number"
                          name="simulatorSlotIntervalMinutes"
                          value={settings.simulatorSlotIntervalMinutes}
                          onChange={handleInputChange}
                          min="5"
                          max="60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Elérhető szimulátorok száma
                        </label>
                        <input
                          type="number"
                          name="availableSimulators"
                          value={settings.availableSimulators}
                          onChange={handleInputChange}
                          min="1"
                          max="20"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Játszóház beállítások */}
            <div className="p-6 bg-white rounded-lg shadow-sm border-t-4 border-t-green-500 border-r border-b border-l border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-medium text-gray-900">Játszóház beállítások</h2>
              </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Foglalás hossza (perc)
                        </label>
                        <input
                          type="number"
                          name="playhouseSlotDurationMinutes"
                          value={settings.playhouseSlotDurationMinutes}
                          onChange={handleInputChange}
                          min="5"
                          max="60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Időközök (perc)
                        </label>
                        <input
                          type="number"
                          name="playhouseSlotIntervalMinutes"
                          value={settings.playhouseSlotIntervalMinutes}
                          onChange={handleInputChange}
                          min="5"
                          max="60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Elérhető játszóházak száma
                        </label>
                        <input
                          type="number"
                          name="availablePlayhouses"
                          value={settings.availablePlayhouses}
                          onChange={handleInputChange}
                          min="1"
                          max="20"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          required
                        />
                      </div>
                    </div>
                  </div>
          </div>
        );
      
      case 'schedule':
        return (
          <div>
            <div className="mb-2 px-4 py-3 bg-blue-50 rounded-lg text-blue-700 text-sm">
              Itt állíthatod be a heti nyitvatartási rendet minden napra külön-külön.
            </div>

            <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-200">
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Heti nyitvatartás</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                      const schedule = settings.schedule[day as keyof SettingsData['schedule']];
                      return (
                        <div key={day} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-md font-medium text-gray-700">
                              {getDayName(day)}
                            </h3>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={schedule.isOpen}
                                onChange={(e) => handleScheduleChange(day as keyof SettingsData['schedule'], 'isOpen', e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                              />
                              <span className="ml-2 text-sm text-gray-600">Nyitva</span>
                            </label>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Nyitás
                              </label>
                              <input
                                type="number"
                                value={schedule.openingHour}
                                onChange={(e) => handleScheduleChange(day as keyof SettingsData['schedule'], 'openingHour', parseInt(e.target.value))}
                                min="0"
                                max="23"
                                disabled={!schedule.isOpen}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-md ${!schedule.isOpen ? 'bg-gray-100' : ''}`}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Zárás
                              </label>
                              <input
                                type="number"
                                value={schedule.closingHour}
                                onChange={(e) => handleScheduleChange(day as keyof SettingsData['schedule'], 'closingHour', parseInt(e.target.value))}
                                min="0"
                                max="23"
                                disabled={!schedule.isOpen}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-md ${!schedule.isOpen ? 'bg-gray-100' : ''}`}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
            </div>
          </div>
        );

      case 'privateEvents':
        return (
          <div>
            <div className="mb-2 px-4 py-3 bg-blue-50 rounded-lg text-blue-700 text-sm">
              Itt kezelheted a zártkörű időszakokat, amelyek során egyes szolgáltatások nem elérhetőek a normál foglalásokhoz.
            </div>

            <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Zártkörű időszakok</h2>
              
              {/* Információ a Zártkörű események nyomtatásáról */}
              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="font-medium text-blue-800 mb-2">Zártkörű események nyomtatása</h3>
                  <p className="text-sm text-blue-700 mb-4">
                    A zártkörű események nyomtatásához és szűréséhez használd a dedikált oldalt, ahol átláthatóan megtekintheted és nyomtathatod az összes eseményt.
                  </p>
                  <a 
                    href="/private-events"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 inline-block"
                  >
                    Zártkörű események nyomtatása
                  </a>
                </div>
              </div>

                    {settings.privateBlocks.map((block, index) => (
                      <div key={index} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Szolgáltatás
                            </label>
                            <select
                              value={block.serviceType}
                              onChange={(e) => handleUpdatePrivateBlock(index, 'serviceType', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                              <option value="gokart">Gokart</option>
                              <option value="vr">VR Élmény</option>
                              <option value="simulator">Szimulátor</option>
                              <option value="playhouse">Játszóház</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Dátum
                            </label>
                            <input
                              type="date"
                              value={block.date}
                              onChange={(e) => handleUpdatePrivateBlock(index, 'date', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              min={new Date().toISOString().split('T')[0]}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Kezdés (óra)
                            </label>
                            <input
                              type="number"
                              value={block.startHour}
                              onChange={(e) => {
                                const dayOfWeek = getDayOfWeek(block.date);
                                const daySchedule = settings.schedule[dayOfWeek];
                                const newValue = parseInt(e.target.value);

                                if (daySchedule.isOpen) {
                                  const validValue = Math.max(
                                    daySchedule.openingHour,
                                    Math.min(daySchedule.closingHour - 1, newValue)
                                  );
                                  handleUpdatePrivateBlock(index, 'startHour', validValue);
                                } else {
                                  const validValue = Math.max(0, Math.min(23, newValue));
                                  handleUpdatePrivateBlock(index, 'startHour', validValue);
                                }
                              }}
                              min={block.date ? (settings.schedule[getDayOfWeek(block.date)].isOpen
                                ? settings.schedule[getDayOfWeek(block.date)].openingHour
                                : 0) : 0}
                              max={block.date ? (settings.schedule[getDayOfWeek(block.date)].isOpen
                                ? settings.schedule[getDayOfWeek(block.date)].closingHour - 1
                                : 23) : 23}
                              disabled={!block.date}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md ${!block.date ? 'bg-gray-100' : ''}`}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Befejezés (óra)
                            </label>
                            <input
                              type="number"
                              value={block.endHour}
                              onChange={(e) => {
                                const dayOfWeek = getDayOfWeek(block.date);
                                const daySchedule = settings.schedule[dayOfWeek];
                                const newValue = parseInt(e.target.value);

                                if (daySchedule.isOpen) {
                                  const validValue = Math.max(
                                    block.startHour + 1,
                                    Math.min(daySchedule.closingHour, newValue)
                                  );
                                  handleUpdatePrivateBlock(index, 'endHour', validValue);
                                } else {
                                  const validValue = Math.max(block.startHour + 1, Math.min(24, newValue));
                                  handleUpdatePrivateBlock(index, 'endHour', validValue);
                                }
                              }}
                              min={block.startHour + 1}
                              max={block.date ? (settings.schedule[getDayOfWeek(block.date)].isOpen
                                ? settings.schedule[getDayOfWeek(block.date)].closingHour
                                : 24) : 24}
                              disabled={!block.date}
                              className={`w-full px-3 py-2 border border-gray-300 rounded-md ${!block.date ? 'bg-gray-100' : ''}`}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Időtartam
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                const dayOfWeek = getDayOfWeek(block.date);
                                const daySchedule = settings.schedule[dayOfWeek];
                                if (daySchedule.isOpen) {
                                  handleUpdatePrivateBlock(index, 'startHour', daySchedule.openingHour);
                                  handleUpdatePrivateBlock(index, 'endHour', daySchedule.closingHour);
                                } else {
                                  handleUpdatePrivateBlock(index, 'startHour', 0);
                                  handleUpdatePrivateBlock(index, 'endHour', 24);
                                }
                              }}
                              disabled={!block.date}
                              className={`w-full px-3 py-2 ${!block.date
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                } rounded-md border ${!block.date ? 'border-gray-200' : 'border-blue-200'}`}
                            >
                              Egész nap
                            </button>
                          </div>
                        </div>

                        <div className="mt-4 flex items-center gap-2">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Leírás
                            </label>
                            <input
                              type="text"
                              value={block.description}
                              onChange={(e) => handleUpdatePrivateBlock(index, 'description', e.target.value)}
                              placeholder="pl.: Céges rendezvény"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                      <p className="mt-1 text-xs text-gray-500">
                        Ez a leírás a nyilvános felületen is megjelenik.
                      </p>
                          </div>
                    <div className="flex items-end mb-[1px]">
                            <button
                              type="button"
                              onClick={() => handleRemovePrivateBlock(index)}
                              className="px-4 py-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 border border-red-200"
                            >
                              Törlés
                            </button>
                          </div>
                        </div>
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Belső jegyzetek
                    </label>
                    <textarea
                      value={(block as any).notes || ''}
                      onChange={(e) => handleUpdatePrivateBlock(index, 'notes' as any, e.target.value)}
                      placeholder="Belső jegyzetek, amelyek csak az adminisztrátorok számára láthatóak"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[80px]"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Ezek a jegyzetek csak adminisztrátorok számára láthatóak, a nyilvános felületen nem jelennek meg.
                    </p>
                  </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={handleAddPrivateBlock}
                      className="mt-4 px-4 py-2 bg-green-50 text-green-600 rounded-md hover:bg-green-100"
                    >
                      + Új zártkörű időszak
                    </button>
                  </div>
          </div>
        );

      case 'api':
        return (
          <div>
            <div className="mb-2 px-4 py-3 bg-blue-50 rounded-lg text-blue-700 text-sm">
              Itt kezelheted a fuvarszervező alkalmazás API hozzáférését, amely lehetővé teszi a foglalási adatok és beállítások lekérését.
            </div>

            <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Fuvarszervező API Hozzáférés</h2>
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm text-gray-600 mb-4">
                        Itt hozhatod létre és kezelheted a fuvarszervező alkalmazás API hozzáférési tokenjét, amely lehetővé teszi a foglalási adatok és beállítások JSON formátumban való lekérését.
                        A token biztosításával a fuvarszervező alkalmazás lokálisan hozzáférhet a napi foglalásokhoz.
                      </p>
                      
                      {settings.apiAccessToken ? (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Fuvarszervező API Végpont URL
                            </label>
                            <div className="flex items-center">
                              <input
                                type="text"
                                value={apiUrl}
                                readOnly
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50"
                              />
                              <button
                                type="button"
                                onClick={copyTokenToClipboard}
                                className={`px-4 py-2 ${tokenCopied ? 'bg-green-600' : 'bg-blue-600'} text-white rounded-r-md`}
                              >
                                {tokenCopied ? 'Másolva!' : 'Másolás'}
                              </button>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              Ez az URL visszaadja a mai napi foglalások és a beállítások adatait JSON formátumban. Másold be a fuvarszervező alkalmazás beállításaihoz.
                            </p>
                            <p className="mt-2 text-xs text-gray-500">
                              <strong>Tipp:</strong> Adott dátum foglalásainak lekéréséhez használd a <code>?token={settings.apiAccessToken}&date=YYYY-MM-DD</code> formátumot,
                              vagy az egyszerűsített <code>?token={settings.apiAccessToken}&date=YYYYMMDD</code> formátumot (pl. 20250515).
                            </p>
                            <p className="mt-2 text-xs text-gray-500">
                              <strong>Szűrés típus szerint:</strong> Csak gokart (vagy más) foglalások lekéréséhez adj hozzá egy <code>&type=gokart</code> paramétert a lekérdezéshez.
                              Lehetséges értékek: <code>gokart</code>, <code>simulator</code>, <code>vr</code>, <code>playhouse</code>.
                            </p>
                          </div>
                          
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={testApiEndpoint}
                              className="px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 border border-blue-200"
                            >
                              Végpont tesztelése
                            </button>
                            <button
                              type="button"
                              onClick={regenerateToken}
                              className="px-4 py-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 border border-red-200"
                            >
                              Token újragenerálása
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-gray-600 mb-4">Még nincs létrehozva API token a fuvarszervező alkalmazás számára. Hozz létre egyet a hozzáféréshez.</p>
                          <button
                            type="button"
                            onClick={generateApiToken}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            Fuvarszervező API Token generálása
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (!currentUser || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation showPublicBooking={true} />

      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-6">Foglalási beállítások</h1>

          {saveSuccess && (
            <div className="mb-6 p-4 bg-green-100 text-green-800 rounded-md">
              A beállítások sikeresen mentve!
            </div>
          )}

          <div className="bg-white overflow-hidden shadow-sm rounded-lg">
            <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <nav className="flex">
                <button
                  onClick={() => setActiveTab('services')}
                  className={`px-6 py-4 text-sm font-medium flex items-center transition-all duration-200 ${
                    activeTab === 'services'
                      ? 'border-b-2 border-blue-500 text-blue-600 bg-white'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-white/50'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
                  </svg>
                  Szolgáltatások
                </button>
                <button
                  onClick={() => setActiveTab('schedule')}
                  className={`px-6 py-4 text-sm font-medium flex items-center transition-all duration-200 ${
                    activeTab === 'schedule'
                      ? 'border-b-2 border-green-500 text-green-600 bg-white'
                      : 'text-gray-600 hover:text-green-600 hover:bg-white/50'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Nyitvatartás
                </button>
                <button
                  onClick={() => setActiveTab('privateEvents')}
                  className={`px-6 py-4 text-sm font-medium flex items-center transition-all duration-200 ${
                    activeTab === 'privateEvents'
                      ? 'border-b-2 border-purple-500 text-purple-600 bg-white'
                      : 'text-gray-600 hover:text-purple-600 hover:bg-white/50'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Zártkörű időszakok
                </button>
                <button
                  onClick={() => setActiveTab('api')}
                  className={`px-6 py-4 text-sm font-medium flex items-center transition-all duration-200 ${
                    activeTab === 'api'
                      ? 'border-b-2 border-yellow-500 text-yellow-600 bg-white'
                      : 'text-gray-600 hover:text-yellow-600 hover:bg-white/50'
                  }`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  API hozzáférés
                </button>
              </nav>
            </div>

            <div className="p-6 bg-gray-50">
              {loading ? (
                <div className="text-center py-4">
                  <p className="text-gray-600">Beállítások betöltése...</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {renderTabContent()}

                  <div className="mt-6 flex justify-end">
                    <button
                      type="submit"
                      disabled={saving}
                      className={`px-4 py-2 rounded-md ${saving
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                      {saving ? 'Mentés...' : 'Beállítások mentése'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get Hungarian day names
const getDayName = (day: string): string => {
  const dayNames: { [key: string]: string } = {
    monday: 'Hétfő',
    tuesday: 'Kedd',
    wednesday: 'Szerda',
    thursday: 'Csütörtök',
    friday: 'Péntek',
    saturday: 'Szombat',
    sunday: 'Vasárnap'
  };
  return dayNames[day] || day;
};

export default Settings; 